import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import apiClient from '../api/client';
import './AprovarImagensNocaute.css';

/**
 * Página para admin aprovar/rejeitar imagens de nocaute enviadas por jogadores
 */

const AprovarImagensNocaute = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [imagens, setImagens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [motivoRejeicao, setMotivoRejeicao] = useState({});
  const [processando, setProcessando] = useState({});

  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }
    carregarImagens();
  }, [isAdmin, navigate]);

  const carregarImagens = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiClient.get('/imagens-nocautes/pendentes');
      setImagens(response.data || []);
    } catch (err) {
      console.error('Erro ao carregar imagens pendentes:', err);
      setError(err.response?.data?.error || 'Erro ao carregar imagens pendentes');
    } finally {
      setLoading(false);
    }
  };

  const handleAprovar = async (id) => {
    try {
      setProcessando({ ...processando, [id]: true });
      setError('');
      setSuccess('');

      await apiClient.put(`/imagens-nocautes/${id}/aprovar`);

      setSuccess('Imagem aprovada com sucesso!');
      setTimeout(() => setSuccess(''), 3000);
      
      // Recarregar lista
      await carregarImagens();
    } catch (err) {
      console.error('Erro ao aprovar imagem:', err);
      setError(err.response?.data?.error || 'Erro ao aprovar imagem');
    } finally {
      setProcessando({ ...processando, [id]: false });
    }
  };

  const handleRejeitar = async (id) => {
    const motivo = motivoRejeicao[id]?.trim() || 'Imagem não atende aos critérios de qualidade.';

    if (!window.confirm(`Tem certeza que deseja rejeitar esta imagem?\n\nMotivo: ${motivo}`)) {
      return;
    }

    try {
      setProcessando({ ...processando, [id]: true });
      setError('');
      setSuccess('');

      await apiClient.put(`/imagens-nocautes/${id}/rejeitar`, {
        motivo_rejeicao: motivo,
      });

      setSuccess('Imagem rejeitada.');
      setTimeout(() => setSuccess(''), 3000);
      
      // Limpar motivo
      setMotivoRejeicao({ ...motivoRejeicao, [id]: '' });
      
      // Recarregar lista
      await carregarImagens();
    } catch (err) {
      console.error('Erro ao rejeitar imagem:', err);
      setError(err.response?.data?.error || 'Erro ao rejeitar imagem');
    } finally {
      setProcessando({ ...processando, [id]: false });
    }
  };

  const formatarTamanho = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const formatarData = (data) => {
    return new Date(data).toLocaleString('pt-BR');
  };

  if (loading) {
    return <div className="container">Carregando imagens pendentes...</div>;
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="container aprovar-imagens-container">
      <div className="page-header">
        <h1>✅ Aprovar Imagens de Nocaute</h1>
        <button onClick={() => navigate('/admin/menu')} className="btn btn-secondary">
          ← Voltar
        </button>
      </div>

      {error && <div className="error">{error}</div>}
      {success && <div className="success">{success}</div>}

      {imagens.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">✅</div>
          <h2>Nenhuma imagem pendente</h2>
          <p>Todas as imagens foram revisadas!</p>
        </div>
      ) : (
        <div className="imagens-list">
          {imagens.map((imagem) => (
            <div key={imagem.id} className="imagem-card">
              <div className="imagem-header">
                <div className="imagem-info-header">
                  <h3>
                    {imagem.tipo === 'nocaute' ? '🥊' : '💀'} {imagem.tipo === 'nocaute' ? 'Nocaute' : 'Eliminação'}
                  </h3>
                  <p className="jogador-info">
                    <strong>Jogador:</strong> {imagem.jogador?.nome || 'N/A'} ({imagem.jogador?.email || 'N/A'})
                  </p>
                  <p className="imagem-meta">
                    <strong>Arquivo:</strong> {imagem.nome_arquivo} ({formatarTamanho(imagem.tamanho_arquivo)})
                  </p>
                  <p className="imagem-meta">
                    <strong>Enviado em:</strong> {formatarData(imagem.createdAt)}
                  </p>
                </div>
              </div>

              <div className="imagem-preview-section">
                {imagem.nome_arquivo?.toLowerCase().endsWith('.mp4') ? (
                  <video
                    src={imagem.url}
                    controls
                    className="imagem-preview-large"
                    style={{ maxWidth: '100%', maxHeight: '400px' }}
                    onError={(e) => {
                      console.error('❌ Erro ao carregar vídeo:', imagem.url);
                      console.error('   URL completa:', imagem.url);
                      if (imagem.caminho_arquivo) {
                        console.error('   Caminho arquivo:', imagem.caminho_arquivo);
                      }
                      if (imagem.fileExists !== undefined) {
                        console.error('   File exists:', imagem.fileExists);
                      }
                      e.target.style.display = 'none';
                      if (e.target.nextSibling) {
                        e.target.nextSibling.style.display = 'block';
                      }
                    }}
                    onLoadedData={() => {
                      console.log('✅ Vídeo carregado:', imagem.url);
                    }}
                  >
                    Seu navegador não suporta vídeos HTML5.
                  </video>
                ) : (
                  <img
                    src={imagem.url}
                    alt={`Imagem de ${imagem.tipo}`}
                    className="imagem-preview-large"
                  onError={(e) => {
                    console.error('❌ Erro ao carregar imagem:', imagem.url);
                    console.error('   URL completa:', imagem.url);
                    if (imagem.caminho_arquivo) {
                      console.error('   Caminho arquivo:', imagem.caminho_arquivo);
                    }
                    if (imagem.fileExists !== undefined) {
                      console.error('   File exists:', imagem.fileExists);
                    }
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                  onLoad={() => {
                    console.log('✅ Imagem carregada:', imagem.url);
                  }}
                  />
                )}
                <div className="imagem-placeholder" style={{ display: 'none' }}>
                  <p>❌ Erro ao carregar imagem</p>
                  <p>O arquivo pode ter sido deletado ou movido.</p>
                  {imagem.caminho_arquivo && (
                    <p style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>
                      <strong>Caminho:</strong> {imagem.caminho_arquivo}
                    </p>
                  )}
                  {imagem.fileExists === false && (
                    <p style={{ fontSize: '12px', color: '#dc3545', marginTop: '5px' }}>
                      ⚠️ Arquivo não encontrado no servidor
                    </p>
                  )}
                  <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                    <strong>URL tentada:</strong> {imagem.url}
                  </p>
                </div>
              </div>

              <div className="imagem-actions">
                <div className="rejeitar-section">
                  <label>
                    <strong>Motivo da rejeição (opcional):</strong>
                    <textarea
                      value={motivoRejeicao[imagem.id] || ''}
                      onChange={(e) => setMotivoRejeicao({ ...motivoRejeicao, [imagem.id]: e.target.value })}
                      placeholder="Digite o motivo da rejeição (opcional)"
                      rows="2"
                      className="motivo-input"
                    />
                  </label>
                  <button
                    onClick={() => handleRejeitar(imagem.id)}
                    className="btn btn-danger"
                    disabled={processando[imagem.id]}
                  >
                    {processando[imagem.id] ? '⏳ Processando...' : '❌ Rejeitar'}
                  </button>
                </div>

                <button
                  onClick={() => handleAprovar(imagem.id)}
                  className="btn btn-success"
                  disabled={processando[imagem.id]}
                >
                  {processando[imagem.id] ? '⏳ Processando...' : '✅ Aprovar'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AprovarImagensNocaute;
